import { t } from 'i18next';
import { LitElement, PropertyValueMap, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import {
  DraftConfigurationListDto,
  deleteDeleteConfiguration,
  getShowDraftConfigurations,
} from '@/api-sdk';
import { FgDialog } from '@/components';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_SMALL_PX } from '@/settings';
import {
  baseStyles,
  buttonStyles,
  rootVariablesStyles,
  typographyStyles,
} from '@/styles';
import { DraftAction } from '@/types';
import { formatDate, getCustomerName, i18nInit, setBaseUrl } from '@/utils';
import {
  iconCart,
  iconCircleInfo,
  iconExclamationTriangle,
  iconTrashCan,
} from '../icons';

import '../fg-dialog';
import '../filters/fg-debtor-list-filters';
import '../loader/fg-loader';
import '../pagination/fg-pagination';

@customElement('fg-draft-configuration-list')
export class FgDraftConfigurationList extends LitElement {
  dialogRef: Ref<FgDialog> = createRef();

  @property({ type: String })
  debtor!: string;

  @property({ type: String })
  customerId!: string;

  @property({ type: Object })
  customers!: { [id: string]: string };

  @property({ type: String })
  baseUrl!: string;

  @property({ type: String })
  locale = 'en_US';

  @property({ type: String })
  country!: string;

  @state()
  searchTerms?: {
    customer?: number;
    searchTermsQuery?: string;
    searchTermsCustomers?: number[];
  };

  @state()
  draftConfigurationPromise?: Promise<DraftConfigurationListDto> | null;

  @state()
  draftConfigurationData?: DraftConfigurationListDto | void;

  @state()
  pages = 1;

  @state()
  currentPage = 1;

  @state()
  modalType?: DraftAction;

  @state()
  selectedConfiguration?: string;

  @state()
  createdByMeActive = false;

  @state()
  error = false;

  @state()
  loading = true;

  @state()
  deletedDraftName?: string;

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  firstUpdated() {
    this.getDraftConfigurations();
  }

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ) {
    if (_changedProperties.has('currentPage') && !this.loading) {
      this.getDraftConfigurations();
    }
  }

  getDraftConfigurations = async (afterDelete = false) => {
    this.loading = true;

    this.draftConfigurationPromise = getShowDraftConfigurations({
      debtorCode: this.debtor,
      params: {
        locale: this.locale,
        page: this.currentPage,
        pageSize: 10,
        country: this.country,
        customer: this.searchTerms?.customer,
        'searchTerms[query]': this.searchTerms?.searchTermsQuery,
        'searchTerms[customers][]': this.searchTerms?.searchTermsCustomers,
      },
    });

    try {
      this.draftConfigurationData = await this.draftConfigurationPromise;

      this.pages = this.draftConfigurationData?.pages;

      if (this.draftConfigurationData && this.error) {
        this.error = false;
      }
    } catch (error) {
      console.error(
        'Something went wrong will fetching draft configuration data:',
        error,
      );
      this.error = true;
    } finally {
      this.loading = false;
      this.draftConfigurationPromise = null;
      if (afterDelete) {
        await this.updateComplete;
        this.openModal(DraftAction.DELETE_COMPLETED);
      }
    }

    this.pages = this.draftConfigurationData?.pages || 1;
  };

  removeDraft = async () => {
    if (this.selectedConfiguration === undefined) {
      return;
    }

    let errorDelete = false;
    this.loading = true;

    try {
      await deleteDeleteConfiguration({
        uuid: this.selectedConfiguration,
      });
    } catch (error) {
      this.loading = false;
      errorDelete = true;
      console.error(
        `Something went wrong while deleting configuration: ${this.selectedConfiguration}`,
        error,
      );
    } finally {
      if (!errorDelete) {
        this.getDraftConfigurations(true);
        errorDelete = false;
      }
    }
  };

  addDraftToCart = () => {
    this.dispatchEvent(
      new CustomEvent('add-to-cart', {
        detail: {
          configurationId: this.selectedConfiguration,
        },
        composed: true,
      }),
    );
  };

  dialogContent = (): {
    [key in DraftAction]: { [key: string]: string };
  } => {
    return {
      [DraftAction.DELETE]: {
        title: t('content.drafts.deleteDraft.title'),
        content: t('content.drafts.deleteDraft.content'),
      },
      [DraftAction.ADD_TO_CART]: {
        title: t('content.drafts.addDraftToCart.title'),
        content: t('content.drafts.addDraftToCart.content'),
      },
      [DraftAction.DELETE_COMPLETED]: {
        title: t('content.drafts.deleteDraftCompleted.title'),
        content: t('content.drafts.deleteDraftCompleted.content', {
          name: this.deletedDraftName,
        }),
      },
    };
  };

  openModal(type: DraftAction, uuid?: string) {
    this.modalType = type;
    if (uuid && uuid.length > 1) {
      const configuration =
        this.draftConfigurationData?.draftConfigurations?.find(
          configuration => configuration.configuration.uuid === uuid,
        );
      if (configuration) {
        this.deletedDraftName = configuration.name;
      }
      this.selectedConfiguration = uuid;
    }

    this.dialogRef.value?.open();
  }

  getFilterParams(
    event: CustomEvent<{ searchString: string | null; createdByMe: boolean }>,
  ) {
    const { searchString, createdByMe } = event.detail;

    if (!searchString && !createdByMe) {
      this.searchTerms = undefined;
      this.getDraftConfigurations();
      return;
    }

    this.createdByMeActive = createdByMe;

    this.searchTerms = {
      searchTermsQuery: searchString || undefined,
      searchTermsCustomers: !this.createdByMeActive
        ? this.checkIfSearchStringIsCustomerName(searchString)
        : [],
      customer: this.createdByMeActive ? parseInt(this.customerId) : undefined,
    };

    this.getDraftConfigurations();
  }

  checkIfSearchStringIsCustomerName(searchString: string | null) {
    const customers: number[] = [];

    if (!searchString) {
      return [];
    }

    Object.keys(this.customers).forEach(key => {
      if (
        this.customers[key].toLowerCase().includes(searchString.toLowerCase())
      ) {
        customers.push(parseInt(key));
      }
    });

    return customers;
  }

  render() {
    if (this.error) {
      return html`
        <div class="notice error">
          ${iconExclamationTriangle} ${t('errors.noDrafts')}
        </div>
      `;
    }

    if (
      !this.loading &&
      (!this.draftConfigurationData ||
        !this.draftConfigurationData.draftConfigurations)
    ) {
      return html`
        <div class="notice info">
          ${iconCircleInfo} ${t('content.drafts.noDrafts')}
        </div>
      `;
    }

    const showConfiguration =
      this.draftConfigurationData?.draftConfigurations.length === 1;

    return html`
      <fg-debtor-list-filters
        @filter=${this.getFilterParams}
        .searchInputPlaceholder=${'content.drafts.searchPlaceholderDrafts'}
        part="wholesale"
        .filterParams=${{
          searchString: this.searchTerms?.searchTermsQuery || undefined,
          createdByMe: this.createdByMeActive,
        }}
      ></fg-debtor-list-filters>
      ${this.loading
        ? html`
            <fg-loader type="configurator"></fg-loader>
          `
        : html`
            <ul class="draft-configuration-list">
              ${this.draftConfigurationData?.draftConfigurations?.length === 0
                ? html`
                    <div class="notice info">
                      ${iconCircleInfo} ${t('content.drafts.noDraftsFound')}
                    </div>
                  `
                : nothing}
              ${this.draftConfigurationData?.draftConfigurations?.map(
                draft => html`
                  <li class="draft-configuration">
                    <div class="draft-configuration-header">
                      <div>
                        <h5>${draft.name}</h5>
                        <span>
                          ${draft.createdAt &&
                          getCustomerName(this.customers, draft.customerId)
                            ? html`
                                ${t('content.drafts.createdDateBy', {
                                  date: formatDate(
                                    draft.createdAt,
                                    this.locale,
                                    'long',
                                  ),
                                  name: getCustomerName(
                                    this.customers,
                                    draft.customerId,
                                  ),
                                })}
                              `
                            : html`
                                ${draft.createdAt &&
                                !getCustomerName(
                                  this.customers,
                                  draft.customerId,
                                )
                                  ? t('content.drafts.createdDate', {
                                      date: formatDate(
                                        draft.createdAt,
                                        this.locale,
                                        'long',
                                      ),
                                    })
                                  : nothing}
                                ${!draft.createdAt &&
                                getCustomerName(
                                  this.customers,
                                  draft.customerId,
                                )
                                  ? t('content.drafts.createdBy', {
                                      name: getCustomerName(
                                        this.customers,
                                        draft.customerId,
                                      ),
                                    })
                                  : nothing}
                              `}
                        </span>
                      </div>
                      <div class="draft-configuration-actions">
                        <button
                          class="button-link"
                          @click=${() =>
                            this.openModal(
                              DraftAction.DELETE,
                              draft.configuration.uuid,
                            )}
                        >
                          ${iconTrashCan} ${t('actions.delete')}
                        </button>
                        <button
                          class="button button-cta"
                          @click=${() =>
                            this.openModal(
                              DraftAction.ADD_TO_CART,
                              draft.configuration.uuid,
                            )}
                        >
                          ${iconCart} ${t('actions.addToShoppingCart')}
                        </button>
                      </div>
                    </div>
                    <fg-configuration
                      part="wholesale"
                      layout="wholesale"
                      .preloadedConfiguration=${draft.configuration}
                      .configuration=${draft.configuration.uuid}
                      showPrintProofButton
                      .showConfigurationDetails=${showConfiguration}
                      .locale=${this.locale}
                    ></fg-configuration>
                  </li>
                `,
              )}
            </ul>
          `}
      ${this.pages > 1
        ? html`
            <fg-pagination
              part="wholesale"
              .pages=${this.pages}
              .currentPage=${this.currentPage}
              @go-to-page=${(event: CustomEvent<{ page: number }>) => {
                const { page } = event.detail;

                if (page) {
                  this.currentPage = page;
                  this.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            ></fg-pagination>
          `
        : nothing}

      <fg-dialog compact inert ${ref(this.dialogRef)} part="wholesale">
        <h2 slot="title">
          ${this.dialogContent()[this.modalType || DraftAction.DELETE].title}
        </h2>
        <div>
          ${unsafeHTML(
            this.dialogContent()[this.modalType || DraftAction.DELETE].content,
          )}
        </div>
        <div
          class="dialog-actions ${classMap({
            'actions-right': this.modalType === DraftAction.DELETE_COMPLETED,
          })}"
          slot="footer"
        >
          <button
            class="button button-tertiary"
            @click=${() => {
              this.dialogRef.value?.close();
              if (this.deletedDraftName) {
                this.deletedDraftName = undefined;
              }
            }}
          >
            ${this.modalType !== DraftAction.DELETE_COMPLETED
              ? t('actions.cancel')
              : t('actions.continue')}
          </button>
          ${this.modalType === DraftAction.ADD_TO_CART
            ? html`
                <button class="button button-cta" @click=${this.addDraftToCart}>
                  ${t('actions.addToShoppingCart')}
                </button>
              `
            : this.modalType === DraftAction.DELETE
              ? html`
                  <button
                    class="button button-primary"
                    @click=${() => this.removeDraft()}
                  >
                    ${t('actions.delete')}
                  </button>
                `
              : nothing}
        </div>
      </fg-dialog>
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    buttonStyles,
    rootVariablesStyles,
    css`
      .draft-configuration {
        border: solid 1px var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        background-color: #ffffff;
        overflow: hidden;
      }

      .draft-configuration-list li:not(:last-of-type) {
        margin-bottom: 1.5rem;
      }

      .draft-configuration-header {
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--300));
        padding: 0.75rem 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        & {
          @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
            width: 100%;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }

          span {
            font-size: 0.875rem;
          }
        }
      }

      .draft-configuration-actions {
        display: flex;
        gap: 1rem;

        && {
          button {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .button-link {
            font-weight: 600;
            text-decoration: none;
          }

          .button-cta {
            white-space: nowrap;
          }

          @media screen and (max-width: ${BREAKPOINT_LARGE_PX - 1}px) {
            justify-content: space-between;
            width: 100%;
          }

          @media screen and (max-width: ${BREAKPOINT_SMALL_PX - 1}px) {
            flex-direction: column;
            align-items: center;
            .button {
              width: 100%;
              justify-content: center;
            }
          }
        }
      }

      fg-configuration {
        display: block;
        padding: 1.5rem;
      }

      fg-pagination {
        margin-top: 1.5rem;
      }

      fg-dialog dialog {
        max-width: 540px;
      }

      .dialog-actions {
        margin-top: 1.5rem;
        display: flex;
        gap: 1rem;

        &.actions-right {
          justify-content: flex-end;
        }

        & .button {
          width: 50%;
        }
      }

      .notice {
        padding: 0.5rem 1rem;
        border: solid 1px var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        display: flex;
        gap: 0.25rem;
        align-items: center;

        &.error {
          color: var(--fg-error, var(--fg-fallback-error));
          background-color: var(
            --fg-error--light,
            var(--fg-fallback-error--light)
          );
          border-color: var(--fg-error, var(--fg-fallback-error));
        }

        &.info {
          color: var(--fg-infobox-content, var(--fg-fallback-infobox-content));
          background-color: var(
            --fg-infobox-background,
            var(--fg-fallback-infobox-background)
          );
          border-color: var(
            --fg-infobox-content,
            var(--fg-fallback-infobox-content)
          );
        }
      }
    `,
  ];
}
